/**
 * AffiliateListsTab - Aba "Afiliados" da Central de Importação de Listas
 *
 * Espelha a UX da DealListsTab (filtros, KPIs implícitos via tabela, ações
 * por linha), porém usando o histórico persistido em `affiliate_import_jobs`
 * via RPCs SECURITY DEFINER.
 *
 * Ações por linha:
 *   - Ver detalhes (drawer com erros) [TODO futuro: por ora, baixar erros]
 *   - Baixar CSV de erros (quando houver `error_summary`)
 *   - Excluir (apenas para status `draft|cancelled|failed|completed_with_errors`;
 *     `completed` e `processing` são bloqueados na RPC).
 *
 * O botão "Nova importação" abre o `ImportAffiliatesWizard` já existente.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  Loader2,
  MoreHorizontal,
  Download,
  Trash2,
  Search,
  X,
  RefreshCw,
  UserPlus,
  PlayCircle,
  Activity,
  Ban,
  Eye,
} from 'lucide-react';
import AffiliateImportPreviewDialog, {
  type AffiliateImportJobSummary,
} from '@/components/referral/import/AffiliateImportPreviewDialog';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { ImportAffiliatesWizard } from '@/components/referral/import/ImportAffiliatesWizard';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useAffiliateImportJobs,
  type AffiliateImportJobRow,
} from '@/hooks/useAffiliateImportJobs';
import { createLogger } from '@/lib/logger';

import { useListExport } from '@/components/export/useListExport';

const logger = createLogger('AffiliateListsTab');

// Mapas alinhados 1:1 com o enum `affiliate_import_status` no banco.
const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  validating: 'bg-blue-500/10 text-blue-600',
  validated: 'bg-cyan-500/10 text-cyan-600',
  awaiting_approval: 'bg-amber-500/10 text-amber-600',
  enqueued: 'bg-indigo-500/10 text-indigo-600',
  processing: 'bg-blue-500/10 text-blue-600',
  completed: 'bg-green-500/10 text-green-600',
  completed_with_errors: 'bg-amber-500/10 text-amber-600',
  failed: 'bg-destructive/10 text-destructive',
  cancelled: 'bg-muted text-muted-foreground',
};

// Cor da bolinha por status — padrão "dot + label preto"
const STATUS_DOT_COLORS: Record<string, string> = {
  draft: 'bg-muted-foreground',
  validating: 'bg-blue-500',
  validated: 'bg-cyan-500',
  awaiting_approval: 'bg-amber-500',
  enqueued: 'bg-indigo-500',
  processing: 'bg-blue-500',
  completed: 'bg-green-500',
  completed_with_errors: 'bg-amber-500',
  failed: 'bg-destructive',
  cancelled: 'bg-muted-foreground',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  validating: 'Validando',
  validated: 'Validado',
  awaiting_approval: 'Aguardando aprovação',
  enqueued: 'Na fila',
  processing: 'Processando',
  completed: 'Concluído',
  completed_with_errors: 'Concluído com erros',
  failed: 'Falhou',
  cancelled: 'Cancelado',
};

// Status que NÃO podem ser excluídos (alinhado com a RPC delete_affiliate_import_job)
const NON_DELETABLE = new Set(['processing', 'completed', 'enqueued', 'validating']);

// Status que permitem retomar a aprovação (abre wizard direto no step 'import')
const RESUMABLE_FOR_APPROVAL = new Set(['awaiting_approval']);

// Status que permitem acompanhar progresso ao vivo / pós-conclusão.
// Espelha CAN_VIEW_PROGRESS de DealListsTab: completed/cancelled não exibem
// "Ver progresso" porque o job já está finalizado e não há mais o que acompanhar.
const VIEWABLE_PROGRESS = new Set([
  'enqueued',
  'processing',
  'completed_with_errors',
  'failed',
]);

// Status canceláveis (mesma lista da RPC cancel_affiliate_import_job)
const CANCELLABLE = new Set([
  'validating',
  'validated',
  'awaiting_approval',
  'enqueued',
  'processing',
]);

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function AffiliateListsTab() {
  const { toast } = useToast();

  // Permissões granulares da aba Afiliados (lists_affiliates)
  const { canCreate, canEdit, canDelete } = usePermissions();
  const canCreateJobs = canCreate('lists_affiliates');
  const canEditJobs = canEdit('lists_affiliates');
  const canDeleteJobs = canDelete('lists_affiliates');

  // Exportação da lista importada (módulo genérico de exportação de dados)
  const { podeExportar, exportarLista, dialogoExportacao } = useListExport('lists_affiliates');
  const [wizardOpen, setWizardOpen] = useState(false);
  // Abre wizard via ?new=1 (atalho "Nova lista de importação (Afiliados)" do Quick Create).
  useAutoOpenFromQuery('1', () => setWizardOpen(true));
  // Quando preenchido, o wizard abre em modo retomado (Continuar aprovação / Ver progresso)
  const [resume, setResume] = useState<{ jobId: string; step: 'import' | 'progress' } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string } | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<{ id: string; name: string } | null>(null);
  const [previewJob, setPreviewJob] = useState<AffiliateImportJobSummary | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Debounce de busca
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { listJobs, deleteJob, cancelJob } = useAffiliateImportJobs({
    search,
    page,
    pageSize,
  });

  const rows: AffiliateImportJobRow[] = listJobs.data ?? [];
  const totalCount = useMemo(
    () => (rows.length > 0 ? Number(rows[0].total_count ?? 0) : 0),
    [rows],
  );

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  /**
   * Faz download do error_summary como CSV.
   * O resumo é mantido pelo hook useAffiliateImport como array de
   * { rowIndex, email, message } — top 50 erros.
   */
  async function handleDownloadErrors(job: AffiliateImportJobRow) {
    try {
      const { data, error } = await (supabase as any).rpc('get_affiliate_import_job', {
        p_job_id: job.id,
      });
      if (error) throw error;
      const summary: Array<{ rowIndex: number; email: string; message: string }> =
        (data as any)?.error_summary ?? [];
      if (!summary || summary.length === 0) {
        toast({ title: 'Nenhum erro para exportar' });
        return;
      }
      const header = 'linha;email;mensagem';
      const lines = summary.map(
        (e) =>
          `${(e.rowIndex ?? '') as any};` +
          `"${(e.email || '').replace(/"/g, '""')}";` +
          `"${(e.message || '').replace(/"/g, '""')}"`,
      );
      const csv = '\ufeff' + [header, ...lines].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `erros-${(job.name || 'importacao').replace(/[^\w-]+/g, '_')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      logger.error('Falha ao baixar CSV de erros', { jobId: job.id, error: err?.message });
      toast({
        title: 'Erro ao gerar CSV',
        description: err?.message ?? 'Falha desconhecida',
        variant: 'destructive',
      });
    }
  }

  async function handleConfirmDelete() {
    if (!confirmDelete) return;
    try {
      await deleteJob.mutateAsync(confirmDelete.id);
      toast({ title: 'Importação excluída' });
    } catch (err: any) {
      toast({
        title: 'Erro ao excluir',
        description: err?.message ?? 'Falha desconhecida',
        variant: 'destructive',
      });
    } finally {
      setConfirmDelete(null);
    }
  }

  async function handleConfirmCancel() {
    if (!confirmCancel) return;
    try {
      await cancelJob.mutateAsync(confirmCancel.id);
      toast({ title: 'Importação cancelada' });
    } catch (err: any) {
      toast({
        title: 'Erro ao cancelar',
        description: err?.message ?? 'Falha desconhecida',
        variant: 'destructive',
      });
    } finally {
      setConfirmCancel(null);
    }
  }

  // Abre o wizard em modo retomado, posicionando no step correto conforme o status.
  function openResume(job: AffiliateImportJobRow, step: 'import' | 'progress') {
    setResume({ jobId: job.id, step });
    setWizardOpen(true);
  }

  return (
    <>
      {/* Barra de ações + filtros */}
      <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b bg-muted/30">
        <div className="flex flex-wrap items-center gap-2 flex-1">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
          {searchInput && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSearchInput('')}
              className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3 mr-1" />
              Limpar
            </Button>
          )}
          {listJobs.isFetching && (
            <span className="text-xs text-muted-foreground animate-pulse">Atualizando...</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="xs"
            onClick={() => listJobs.refetch()}
            disabled={listJobs.isFetching}
          >
            <RefreshCw className={`mr-1.5 ${listJobs.isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          {canCreateJobs && (
            <Button size="xs" onClick={() => setWizardOpen(true)}>
              <Plus className="mr-1.5" />
              Nova importação
            </Button>
          )}
        </div>
      </div>

      <div className="p-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Importações de Afiliados
            </CardTitle>
            <CardDescription>
              Histórico de importações em massa de afiliados via CSV neste tenant.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {listJobs.isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <UserPlus className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">Nenhuma importação encontrada</p>
                <p className="text-sm">
                  {search
                    ? 'Ajuste a busca ou limpe o filtro.'
                    : 'Clique em "Nova importação" para começar.'}
                </p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Programa</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Criados</TableHead>
                      <TableHead className="text-right">Aprovados</TableHead>
                      <TableHead className="text-right" title="Inválidos + duplicados no CSV">
                        Inválidos
                      </TableHead>
                      <TableHead className="text-right" title="Já existiam no sistema">
                        Existentes
                      </TableHead>
                      <TableHead className="text-right">Falhas</TableHead>
                      <TableHead>Criado em</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((j) => {
                      const canDelete = !NON_DELETABLE.has(j.status) && canDeleteJobs;
                      const invalidTotal = (j.invalid_count || 0) + (j.duplicate_count || 0);
                      return (
                        <TableRow key={j.id}>
                          <TableCell className="font-medium">{j.name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {j.program_name ?? '—'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-sm text-foreground">
                              <span className={cn('h-2 w-2 rounded-full', STATUS_DOT_COLORS[j.status] || 'bg-muted-foreground')} />
                              {STATUS_LABELS[j.status] || j.status}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {j.total_rows?.toLocaleString('pt-BR') ?? 0}
                          </TableCell>
                          <TableCell className="text-right text-green-600">
                            {(j.succeeded_count ?? 0).toLocaleString('pt-BR')}
                          </TableCell>
                          <TableCell className="text-right">
                            {(j.approved_count ?? 0).toLocaleString('pt-BR')}
                          </TableCell>
                          <TableCell className="text-right text-amber-600">
                            {invalidTotal.toLocaleString('pt-BR')}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {(j.existing_count ?? 0).toLocaleString('pt-BR')}
                          </TableCell>
                          <TableCell className="text-right text-destructive">
                            {(j.failed_count ?? 0).toLocaleString('pt-BR')}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {j.created_at &&
                              format(new Date(j.created_at), 'dd/MM/yyyy HH:mm', {
                                locale: ptBR,
                              })}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="h-4 w-4" />
                                  <span className="sr-only">Ações</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                {(j.total_rows ?? 0) > 0 && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setPreviewJob({
                                        id: j.id,
                                        name: j.name,
                                        created_at: j.created_at,
                                        total_rows: j.total_rows,
                                        succeeded_count: j.succeeded_count,
                                        existing_count: j.existing_count,
                                        invalid_count: j.invalid_count,
                                        duplicate_count: j.duplicate_count,
                                        failed_count: j.failed_count,
                                      })
                                    }
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    Visualizar amostra
                                  </DropdownMenuItem>
                                )}
                                {RESUMABLE_FOR_APPROVAL.has(j.status) && canEditJobs && (
                                  <DropdownMenuItem onClick={() => openResume(j, 'import')}>
                                    <PlayCircle className="h-4 w-4 mr-2" />
                                    Continuar aprovação
                                  </DropdownMenuItem>
                                )}
                                {VIEWABLE_PROGRESS.has(j.status) && (
                                  <DropdownMenuItem onClick={() => openResume(j, 'progress')}>
                                    <Activity className="h-4 w-4 mr-2" />
                                    Ver progresso
                                  </DropdownMenuItem>
                                )}
                                {podeExportar && (j.total_rows ?? 0) > 0 && (
                                  <DropdownMenuItem
                                    onClick={() => exportarLista({ refId: j.id, name: j.name })}
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    Exportar lista
                                  </DropdownMenuItem>
                                )}
                                {CANCELLABLE.has(j.status) && canEditJobs && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setConfirmCancel({ id: j.id, name: j.name })
                                    }
                                  >
                                    <Ban className="h-4 w-4 mr-2" />
                                    Cancelar importação
                                  </DropdownMenuItem>
                                )}
                                {j.has_errors && (
                                  <DropdownMenuItem onClick={() => handleDownloadErrors(j)}>
                                    <Download className="h-4 w-4 mr-2" />
                                    Baixar CSV de erros
                                  </DropdownMenuItem>
                                )}
                                {canDelete && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setConfirmDelete({ id: j.id, name: j.name })
                                    }
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Excluir
                                  </DropdownMenuItem>
                                )}
                                {!j.has_errors
                                  && !canDelete
                                  && !RESUMABLE_FOR_APPROVAL.has(j.status)
                                  && !VIEWABLE_PROGRESS.has(j.status)
                                  && !CANCELLABLE.has(j.status) && (
                                  <DropdownMenuItem disabled>
                                    Sem ações disponíveis
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                <div className="border-t mt-2 px-4 py-2 bg-background">
                  <PaginationFooter
                    totalItems={totalCount}
                    currentPage={page}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    borderClassName=""
                    className="mt-0 pt-0"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <ImportAffiliatesWizard
        open={wizardOpen}
        resume={resume}
        onOpenChange={(v) => {
          setWizardOpen(v);
          if (!v) {
            setResume(null);
            listJobs.refetch();
          }
        }}
      />

      <AlertDialog open={!!confirmCancel} onOpenChange={(v) => !v && setConfirmCancel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar importação?</AlertDialogTitle>
            <AlertDialogDescription>
              Os itens já processados de <strong>{confirmCancel?.name}</strong> serão
              mantidos. Os lotes pendentes na fila serão descartados e o job marcado
              como cancelado. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelJob.isPending}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              disabled={cancelJob.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelJob.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Cancelar importação
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir importação?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação remove permanentemente o registro <strong>{confirmDelete?.name}</strong>{' '}
              e o arquivo CSV armazenado. Os afiliados já cadastrados <strong>não</strong> serão
              afetados. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteJob.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteJob.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Excluir definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {previewJob && (
        <AffiliateImportPreviewDialog
          job={previewJob}
          open={!!previewJob}
          onOpenChange={(o) => !o && setPreviewJob(null)}
        />
      )}

      {/* Modal genérico de exportação da lista selecionada */}
      {dialogoExportacao}
    </>
  );
}
